import { BrandImageTag, MAX_BRAND_IMAGES } from '@mondaygpt/constants'
import { Button } from 'components'
import { cn } from 'lib/utils'
import { Loader2, Plus, X } from 'lucide-react'
import { useImageUpload } from 'modules/common/hooks/use-file-upload'
import type { BrandImage } from 'modules/creative/types'
import { useEffect, useRef, useState } from 'react'
import { CONTENT_FOCUS_OPTIONS } from './content-focus'
import { Eyebrow } from './Eyebrow'
import { MobileStickyFooter } from './MobileStickyFooter'

interface FocusStepProps {
  /** `contentFocus` is pre-selected by the scrape's auto-classifier; user can change. */
  initial: { contentFocus: BrandImageTag | null; images: BrandImage[] }
  isSubmitting?: boolean
  onSubmit: (data: { contentFocus: BrandImageTag; images: BrandImage[] }) => void
}

const MAX_IMAGE_BYTES = 10 * 1024 * 1024

/**
 * Phase 4. The user picks their archetype (products / people / other) — which
 * drives downstream image-ask UX — and optionally drops in a few photos so
 * Taka has real source material to pull from on day one.
 *
 * Photos are optional: the CTA stays enabled even with zero uploads. The
 * field is mostly here so users with strong product/personal brand can seed
 * Taka immediately; service businesses without ready photos can skip and add
 * later in brand kit settings.
 */
export const FocusStep: React.FC<FocusStepProps> = ({ initial, isSubmitting, onSubmit }) => {
  // Default to 'other' if the scan didn't classify (e.g., name-only flow).
  const [focus, setFocus] = useState<BrandImageTag>(initial.contentFocus ?? 'other')
  const [images, setImages] = useState<BrandImage[]>(initial.images)
  const { handleUpload } = useImageUpload({ maxSize: MAX_IMAGE_BYTES })
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Revoke any in-flight blob URLs if the user navigates away mid-upload.
  const imagesRef = useRef(images)
  imagesRef.current = images
  useEffect(
    () => () => {
      imagesRef.current.forEach(img => {
        if (img.publicUrl?.startsWith('blob:')) URL.revokeObjectURL(img.publicUrl)
      })
    },
    []
  )

  // Shared upload pipeline — used by both the file picker and the drop handler.
  // Filters to images only (the picker has `accept="image/*"` but drag-and-drop
  // bypasses that constraint), enforces MAX_BRAND_IMAGES, and runs the same
  // optimistic blob-URL → real-attachment swap so visual feedback is identical.
  const processFiles = async (files: File[]) => {
    const imageFiles = files.filter(f => f.type.startsWith('image/'))
    if (!imageFiles.length) return

    const remaining = MAX_BRAND_IMAGES - images.length
    const pending = imageFiles.slice(0, remaining).map(file => ({
      file,
      blobUrl: URL.createObjectURL(file),
    }))
    if (!pending.length) return

    setImages(prev => [
      ...prev,
      ...pending.map(({ blobUrl }) => ({
        attachmentId: '',
        tags: [focus],
        publicUrl: blobUrl,
      })),
    ])

    await Promise.allSettled(
      pending.map(async ({ file, blobUrl }) => {
        try {
          const attachment = await handleUpload(file)
          URL.revokeObjectURL(blobUrl)
          setImages(prev =>
            prev.map(img =>
              img.publicUrl === blobUrl
                ? { ...img, attachmentId: attachment.id, publicUrl: attachment.publicUrl }
                : img
            )
          )
        } catch (err) {
          console.warn('[FocusStep] upload failed:', err)
          URL.revokeObjectURL(blobUrl)
          setImages(prev => prev.filter(img => img.publicUrl !== blobUrl))
        }
      })
    )
  }

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    e.target.value = ''
    await processFiles(files)
  }

  // Drag-and-drop on the upload card. A simple boolean would flicker as the
  // user drags over child elements (each fires its own enter/leave), so we
  // track a counter and only toggle highlight when it crosses zero. This is
  // the standard React-DnD-without-libs pattern.
  const dragCounter = useRef(0)
  const [isDragOver, setIsDragOver] = useState(false)

  const handleDragEnter = (e: React.DragEvent) => {
    if (!e.dataTransfer?.types.includes('Files')) return
    e.preventDefault()
    dragCounter.current += 1
    if (dragCounter.current === 1) setIsDragOver(true)
  }

  const handleDragOver = (e: React.DragEvent) => {
    if (!e.dataTransfer?.types.includes('Files')) return
    // Required for `drop` to fire — without preventDefault the browser will
    // open the file instead of letting our handler run.
    e.preventDefault()
  }

  const handleDragLeave = (e: React.DragEvent) => {
    if (!e.dataTransfer?.types.includes('Files')) return
    e.preventDefault()
    dragCounter.current = Math.max(0, dragCounter.current - 1)
    if (dragCounter.current === 0) setIsDragOver(false)
  }

  const handleDrop = async (e: React.DragEvent) => {
    if (!e.dataTransfer?.types.includes('Files')) return
    e.preventDefault()
    dragCounter.current = 0
    setIsDragOver(false)
    await processFiles(Array.from(e.dataTransfer.files))
  }

  const handleRemove = (publicUrl: string | undefined) => {
    if (!publicUrl) return
    if (publicUrl.startsWith('blob:')) URL.revokeObjectURL(publicUrl)
    setImages(prev => prev.filter(i => i.publicUrl !== publicUrl))
  }

  const handleFocusChange = (next: BrandImageTag) => {
    setFocus(next)
    setImages(prev => prev.map(img => ({ ...img, tags: [next] })))
  }

  const isUploading = images.some(img => !img.attachmentId)
  const selectedOption = CONTENT_FOCUS_OPTIONS.find(o => o.id === focus) ?? CONTENT_FOCUS_OPTIONS[2]
  const canAddMore = images.length < MAX_BRAND_IMAGES

  return (
    <div className="mx-auto flex max-w-[760px] flex-col gap-6">
      <div className="flex flex-col gap-3 px-1">
        <Eyebrow>Your visuals</Eyebrow>
        <h2 className="text-[32px] leading-[1.05] font-semibold tracking-[-0.02em] text-paper sm:text-[40px]">
          What goes in your posts?
        </h2>
        <p className="lede max-w-[520px] !text-[15px] !leading-[1.55]">
          Pick what we'll feature, then drop in a few photos for Taka to pull from.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {CONTENT_FOCUS_OPTIONS.map(opt => (
          <FocusCard
            key={opt.id}
            label={opt.label}
            blurb={opt.blurb}
            active={focus === opt.id}
            onClick={() => handleFocusChange(opt.id)}
          />
        ))}
      </div>

      <div
        className={cn(
          'card flex flex-col gap-4 p-5 transition-colors sm:p-6',
          isDragOver && canAddMore && 'bg-acid/5 ring-2 ring-acid'
        )}
        onDragEnter={canAddMore ? handleDragEnter : undefined}
        onDragOver={canAddMore ? handleDragOver : undefined}
        onDragLeave={canAddMore ? handleDragLeave : undefined}
        onDrop={canAddMore ? handleDrop : undefined}
      >
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-[14px] font-medium text-paper">
            {isDragOver && canAddMore ? 'Drop to upload' : selectedOption.uploadCopy}
          </span>
          <span className="mono text-[10px] tracking-[0.1em] text-paper-faint uppercase">
            {images.length}/{MAX_BRAND_IMAGES}
          </span>
        </div>

        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {images.map(img => (
            <ImageThumb
              key={img.publicUrl ?? img.attachmentId}
              img={img}
              onRemove={() => handleRemove(img.publicUrl)}
            />
          ))}
          {canAddMore && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex aspect-square items-center justify-center rounded-[10px] border border-dashed border-line-bright text-paper-dim transition-colors hover:border-acid hover:bg-acid/5 hover:text-acid"
              aria-label="Add photos"
            >
              <Plus className="h-5 w-5" strokeWidth={2} />
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>

        <p className="text-[12px] text-paper-faint">
          Optional. You can always add more from your brand kit settings.
        </p>
      </div>

      <MobileStickyFooter>
        <Button
          variant="t3-primary"
          size="t3-lg"
          disabled={isUploading || isSubmitting}
          aria-busy={isSubmitting}
          onClick={() => onSubmit({ contentFocus: focus, images })}
          className="!max-w-[420px]"
        >
          {isSubmitting ? 'Saving…' : isUploading ? 'Uploading…' : 'Continue →'}
        </Button>
      </MobileStickyFooter>
    </div>
  )
}

const FocusCard: React.FC<{
  label: string
  blurb: string
  active: boolean
  onClick: () => void
}> = ({ label, blurb, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-active={active}
    aria-pressed={active}
    className="select-card flex flex-col gap-2 rounded-[12px] p-4 text-left"
  >
    <span
      className={cn(
        'text-[15px] font-semibold tracking-[-0.01em]',
        active ? 'text-acid' : 'text-paper'
      )}
    >
      {label}
    </span>
    <span className="text-[12.5px] leading-[1.45] text-paper-dim">{blurb}</span>
  </button>
)

const ImageThumb: React.FC<{ img: BrandImage; onRemove: () => void }> = ({ img, onRemove }) => {
  const stillUploading = !img.attachmentId
  return (
    <div className="group relative aspect-square overflow-hidden rounded-[10px] border border-line-bright bg-bg-3">
      {img.publicUrl && <img src={img.publicUrl} alt="" className="h-full w-full object-cover" />}
      {stillUploading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/40">
          <Loader2 className="h-5 w-5 animate-spin text-acid" strokeWidth={2.4} />
        </div>
      )}
      <button
        type="button"
        onClick={onRemove}
        className="absolute top-1 right-1 flex h-6 w-6 items-center justify-center rounded-full bg-bg/80 text-paper-dim opacity-0 transition-opacity group-hover:opacity-100 hover:bg-bg hover:text-paper"
        aria-label="Remove image"
      >
        <X className="h-3.5 w-3.5" strokeWidth={2.4} />
      </button>
    </div>
  )
}
